import http from "../http";
import { useQuery, useMutation } from "../../contexts/HttpQueryContext";

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useGetNotifications({
  page = 1,
  enabled = true,
  token = null,
  options = {},
} = {}) {
  return useQuery({
    ...options,
    enabled: enabled && Boolean(token),
    queryKey: ["App::Notifications::List", page],
    queryFn: async () => {
      const url = http.url("app.notifications.list");
      const response = await http.api.get(url, {
        params: { page },
        headers: authHeaders(token),
      });
      return response.data;
    },
  });
}

const MAX_PAGES_TO_AGGREGATE = 50;

export function useGetAllNotifications({
  enabled = true,
  token = null,
  options = {},
} = {}) {
  return useQuery({
    ...options,
    enabled: enabled && Boolean(token),
    queryKey: ["App::Notifications::All"],
    queryFn: async () => {
      const url = http.url("app.notifications.list");
      const headers = authHeaders(token);

      const firstResponse = await http.api.get(url, { params: { page: 1 }, headers });
      const firstResult = firstResponse.data?.result ?? {};
      const lastPage = Math.min(
        Number(firstResult.pagination?.last_page ?? 1) || 1,
        MAX_PAGES_TO_AGGREGATE
      );

      const items = [...(firstResult.notifications ?? [])];

      if (lastPage > 1) {
        const remainingPages = Array.from({ length: lastPage - 1 }, (_, i) => i + 2);
        const responses = await Promise.all(
          remainingPages.map((p) => http.api.get(url, { params: { page: p }, headers }))
        );
        for (const res of responses) {
          const r = res.data?.result ?? {};
          if (Array.isArray(r.notifications)) items.push(...r.notifications);
        }
      }

      return {
        result: {
          notifications: items,
          unread_count: firstResult.unread_count ?? 0,
          pagination: {
            current_page: 1,
            last_page: 1,
            per_page: items.length,
            total: items.length,
          },
        },
      };
    },
  });
}

export function useMarkNotificationAsRead({ token = null } = {}) {
  return useMutation({
    mutationFn: async (guid) => {
      const url = http.url("app.notifications.markAsRead", { guid });
      const response = await http.api.get(url, {
        headers: authHeaders(token),
      });
      return response.data;
    },
  });
}
